import { Button, Icon, Text, TextareaV2 } from '@southfields-digital/mxmz-react-ui';
import { useState } from 'react';

import { Member } from 'src/types/matchControl/member';

import { LineUpParseSummary, parseLineUpPaste } from './parseLineUpPaste';

type PasteMembersProps = {
  onImport: (members: Member[]) => void;
};

const PLACEHOLDER =
  'Paste data rows copied from a spreadsheet — do not include the header row.\n' +
  'Column order: Number, Name, Type, Goals, Caps, Matches, Age\n\n' +
  'For example:\n' +
  '7\tAlice Foster\tstarter\t0\t50\t4\t29\n' +
  '8\tBar Bench\tbench\t2\t10\t9\t22';

const PasteMembers = ({ onImport }: PasteMembersProps) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [summary, setSummary] = useState<LineUpParseSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setText('');
    setError(null);
    setSummary(null);
  };

  const handleToggle = () => {
    setOpen((prev) => !prev);
    reset();
  };

  const handleChangeText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    if (error) setError(null);
    if (summary) setSummary(null);
  };

  const handleImport = () => {
    const { members, summary: result } = parseLineUpPaste(text);

    if (members.length === 0) {
      setError("Couldn't find any rows to import in the pasted content.");
      setSummary(null);
      return;
    }

    onImport(members);
    setSummary(result);
    setError(null);
    setText('');
  };

  return (
    <div className="flex flex-col gap-2">
      <Button variant="secondary" onClick={handleToggle}>
        <div className="flex items-center gap-2">
          <Icon icon={open ? 'chevron-up' : 'clipboard'} />
          {open ? 'Close paste' : 'Paste from spreadsheet'}
        </div>
      </Button>

      {open && (
        <div className="flex flex-col gap-4 rounded-[4px] border border-gray-800 bg-elevated-0 p-3">
          <TextareaV2
            type="textarea"
            inputSize="lg"
            rows={6}
            value={text}
            placeholder={PLACEHOLDER}
            onChange={handleChangeText}
          />
          <div className="flex gap-2 justify-between items-center">
            <div className="flex items-center gap-1.5 text-sm">
              {error && (
                <>
                  <Icon icon="warning-circle" className="!text-red-400" />
                  <Text as="span" className="!text-red-400">
                    {error}
                  </Text>
                </>
              )}
              {!error && summary && (
                <>
                  <Icon icon="check-circle" className="!text-green-400" />
                  <Text as="span">
                    Imported {summary.importedCount} member{summary.importedCount === 1 ? '' : 's'}
                  </Text>
                  {summary.issueCount > 0 && (
                    <>
                      <Icon icon="warning-circle" className="!text-amber-400 ml-2" />
                      <Text as="span" className="!text-amber-400">
                        {summary.issueCount} with missing or invalid data
                      </Text>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="flex justify-end">
              <Button onClick={handleImport} color="primaryLight" disabled={text.trim() === ''}>
                Import rows
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PasteMembers;
